import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthorizationRole, Permission, UserAuthorizationPermissions, UserPermissions } from '../models';

/** Droits effectifs de l'utilisateur connecté (UC-05), portés par authorization-service. */
@Injectable({ providedIn: 'root' })
export class AuthorizationService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/authorization-service/api/v1/authorization/me`;

  /**
   * Rôles et permissions effectifs. À appeler juste après login puis conserver en mémoire
   * (voir AuthService.fetchCurrentUser) : authorization-service consolide les affectations de
   * rôles locales avec les rôles portés par le JWT, donc plus fiable que le seul JWT lorsqu'un
   * rôle a été modifié côté back-office sans réémission de token.
   */
  myPermissions(): Observable<UserPermissions> {
    return this.http.get<UserPermissions>(`${this.baseUrl}/permissions`);
  }

  /** Liste des rôles et permissions affichés dans le back-office. */
  roles(): Observable<AuthorizationRole[]> {
    return this.http.get<AuthorizationRole[]>(
      `${environment.apiUrl}/authorization-service/api/v1/authorization/roles`,
    );
  }

  role(code: string): Observable<AuthorizationRole> {
    return this.http.get<AuthorizationRole>(
      `${environment.apiUrl}/authorization-service/api/v1/authorization/roles/${code}`,
    );
  }

  createRole(payload: {
    code: string;
    name: string;
    description?: string | null;
    active?: boolean;
  }): Observable<AuthorizationRole> {
    return this.http.post<AuthorizationRole>(`${environment.apiUrl}/authorization-service/api/v1/authorization/roles`, payload);
  }

  createPermission(payload: {
    code: string;
    name: string;
    description?: string | null;
    moduleCode: string;
    resourceCode: string;
    actionCode: string;
    active?: boolean;
  }): Observable<Permission> {
    return this.http.post<Permission>(`${environment.apiUrl}/authorization-service/api/v1/authorization/permissions`, payload);
  }

  bulkRoles(payload: {
    userIds: string[];
    roleCodes: string[];
    assignedBy: string;
    expiresAt?: string | null;
  }): Observable<Record<string, UserAuthorizationPermissions>> {
    return this.http.post<Record<string, UserAuthorizationPermissions>>(
      `${environment.apiUrl}/authorization-service/api/v1/authorization/users/bulk-roles`,
      payload,
    );
  }

  /** Retourne les userId qui portent actuellement ce rôle. */
  roleUsers(code: string): Observable<string[]> {
    return this.http.get<string[]>(
      `${environment.apiUrl}/authorization-service/api/v1/authorization/roles/${code}/users`,
    );
  }

  /** Catalogue des permissions disponibles dans authorization-service. */
  permissions(): Observable<Permission[]> {
    return this.http.get<Permission[]>(`${environment.apiUrl}/authorization-service/api/v1/authorization/permissions`);
  }

  /** Rôles et permissions effectifs pour un utilisateur précis. */
  userPermissions(userId: string): Observable<UserAuthorizationPermissions> {
    return this.http.get<UserAuthorizationPermissions>(
      `${environment.apiUrl}/authorization-service/api/v1/authorization/users/${userId}/permissions`,
    );
  }

  /** Vérification serveur d'une permission précise, à utiliser juste avant une action critique. */
  checkPermission(permissionCode: string): Observable<boolean> {
    const params = new HttpParams().set('permission', permissionCode);
    return this.http
      .get<boolean | { granted: boolean }>(`${this.baseUrl}/check`, { params })
      .pipe(map((res) => (typeof res === 'boolean' ? res : !!res?.granted)));
  }

  replaceUserRoles(
    userId: string,
    payload: {
      roleCodes: string[];
      assignedBy: string;
      expiresAt?: string | null;
    },
  ): Observable<UserAuthorizationPermissions> {
    return this.http.put<UserAuthorizationPermissions>(
      `${environment.apiUrl}/authorization-service/api/v1/authorization/users/${userId}/roles`,
      payload,
    );
  }

  /** Remplace la matrice de permissions d'un role par la liste exacte fournie. */
  replaceRolePermissions(
    roleCode: string,
    payload: {
      permissionCodes: string[];
    },
  ): Observable<AuthorizationRole> {
    return this.http.put<AuthorizationRole>(
      `${environment.apiUrl}/authorization-service/api/v1/authorization/roles/${roleCode}/permissions`,
      payload,
    );
  }
}
